import type { Edge, Node, Scene } from '../../../core/models/scene';
import type {
  PositionComputedState,
  OrgModelEntity,
  OrgModelProjection,
} from '../models/OrgModelProjection';
import {
  DEFAULT_ORG_PERSON_NODE_TYPE,
  isOrgPersonNodeType,
  resolveOrgChartViewMode,
  type OrgChartViewMode,
} from '../models/defaults';
import { ORG_PERSON_CARD_SPECS, resolveOrgPersonCardDimensions } from '../models/orgChartShapeVariants';
import { isProjectedOrgPersonCard } from '../constants/orgNodeTypes';
import { positionStaffingAccent } from '../components/positionStaffingCard';
import { computeFullTreeLayout } from './graphAnalysis';
import {
  applyOrgChartCollapse,
  computeAutoCollapseIds,
  EDITOR_AUTO_COLLAPSE_THRESHOLD,
  VISIBLE_DEPTH,
} from './orgChartCollapseProjection';
import {
  departmentMembership,
} from './PeopleChartDepartmentProjectionService';
import { buildPeopleChartReducedMembership } from './PeopleChartReducedMembershipProjection';
import { getRelationshipMapping } from '../../../lib/diagram/registries';
export {
  departmentMembership,
  listPeopleChartDepartments,
  listPeopleChartDepartmentsWithCounts,
} from './PeopleChartDepartmentProjectionService';

type PeopleChartLayoutSpacing = {
  readonly nodeSpacing: number | undefined;
  readonly verticalSpacing: number | undefined;
};

const peopleChartLayoutSpacing = new WeakMap<Scene, PeopleChartLayoutSpacing>();

export interface PeopleChartViewDefinition {
  readonly filters?: Record<string, unknown>;
  /** Ephemeral, already-authorized membership for a server-owned saved view. */
  readonly matchingPersonIds?: readonly string[];
}

/** Non-sensitive presentation prefs for locked live People Charts. */
export interface PeopleChartPresentation {
  readonly personNodeType?: string;
  readonly viewMode?: OrgChartViewMode | string | null;
  readonly cardAccent?: string | null;
  readonly nodeSpacing?: number | null;
  readonly verticalSpacing?: number | null;
}

interface PeopleHierarchy {
  readonly edges: Edge[];
  readonly matrixEdges: Edge[];
  readonly managerByPersonId: ReadonlyMap<string, string>;
  readonly positionByPersonId: ReadonlyMap<string, OrgModelEntity>;
  readonly seatsByPersonId: ReadonlyMap<string, readonly OrgModelEntity[]>;
}

interface InitialPeopleChartLayoutOptions {
  readonly autoCollapseThreshold?: number;
  readonly visibleDepth?: number;
  readonly nodeSpacing?: number;
  readonly verticalSpacing?: number;
}

/**
 * Computes first-paint positions for a source-backed People Chart.
 *
 * Large charts are auto-collapsed by the canvas after hydration. Reflow the
 * visible projection here as well so the surviving cards do not retain the
 * sparse coordinates calculated for hidden descendants.
 */
export function computeInitialPeopleChartLayout(
  nodes: Node[],
  edges: Edge[],
  options: InitialPeopleChartLayoutOptions = {},
): Map<string, { x: number; y: number }> {
  const layoutOpts = {
    nodeSpacing: options.nodeSpacing,
    verticalSpacing: options.verticalSpacing,
  };
  const positions = computeFullTreeLayout(nodes, edges, layoutOpts);
  const collapsedNodeIds = computeAutoCollapseIds(
    nodes,
    edges,
    options.autoCollapseThreshold ?? EDITOR_AUTO_COLLAPSE_THRESHOLD,
    options.visibleDepth ?? VISIBLE_DEPTH,
  );
  if (collapsedNodeIds.length === 0) return positions;

  const visibleProjection = applyOrgChartCollapse(nodes, edges, collapsedNodeIds);
  const visiblePositions = computeFullTreeLayout(
    visibleProjection.nodes,
    visibleProjection.edges,
    layoutOpts,
  );
  visiblePositions.forEach((position, nodeId) => positions.set(nodeId, position));
  return positions;
}

/**
 * Builds a caller-specific People Chart projection in browser memory.
 * Canonical values are never copied into the diagram Y.Doc.
 */
export function buildPeopleChartScene(
  model: OrgModelProjection,
  viewDefinition?: PeopleChartViewDefinition | null,
  previous?: Scene,
  presentation?: PeopleChartPresentation | null,
): Scene {
  const { personNodeType, viewMode, cardAccent, nodeSpacing, verticalSpacing } =
    resolvePeopleChartPresentation(presentation);
  const layoutOpts = { nodeSpacing, verticalSpacing };
  const hierarchy = buildPeopleHierarchy(model);
  const departmentKey = stringValue(viewDefinition?.filters?.departmentKey);
  const membership = departmentMembership(model, hierarchy.positionByPersonId);
  const reducedMembership = buildPeopleChartReducedMembership({
    personIds: model.people.map((person) => person.id),
    matchingPersonIds: viewDefinition?.matchingPersonIds ?? (departmentKey
      ? model.people
        .filter((person) => membership.get(person.id)?.key === departmentKey)
        .map((person) => person.id)
      : model.people.map((person) => person.id)),
    managerByPersonId: hierarchy.managerByPersonId,
  });
  const includedIds = reducedMembership.includedPersonIds;
  const directReportCounts = new Map<string, number>();
  for (const edge of hierarchy.edges) {
    if (!includedIds.has(edge.from) || !includedIds.has(edge.to)) continue;
    directReportCounts.set(edge.to, (directReportCounts.get(edge.to) ?? 0) + 1);
  }
  const talentByPerson = talentAttributes(model);
  const positionStateById = new Map(model.positionState.map((state) => [state.positionId, state]));
  // Position-backed charts must never resurrect an imported role. Only a
  // complete occupancy read can distinguish unassigned from unavailable.
  const seatedChart = model.positions.length > 0;
  const seatlessRole = model.occupancyVisibility === 'visible' ? 'Unassigned' : 'Role unavailable';
  const peopleNodes = model.people
    .filter((person) => includedIds.has(person.id))
    .map((person) => {
      const position = hierarchy.positionByPersonId.get(person.id);
      const positionState = position ? positionStateById.get(position.id) : undefined;
      const department = membership.get(person.id);
      const directReports = directReportCounts.get(person.id) ?? 0;
      const dimensions = resolveOrgPersonCardDimensions(
        personNodeType,
        viewMode,
        directReports > 0,
      );
      return {
        id: person.id,
        type: personNodeType,
        version: 1,
        category: 'orgchart',
        x: 0,
        y: 0,
        width: dimensions.width,
        height: dimensions.height,
        w: dimensions.width,
        h: dimensions.height,
        name: person.label,
        text: person.label,
        locked: true,
        lockOwner: 'projection',
        strokeColor: cardAccent,
        attrs: {
          ...visibleAttributes(person.props),
          ...personPositionWorkContext(person.props, position),
          ...talentByPerson.get(person.id),
          folderRecordId: person.id,
          ...(position ? { reportingPositionId: position.id } : {}),
          ...personRoleAttributes(hierarchy.seatsByPersonId.get(person.id) ?? [], position),
          personId: person.externalId ?? person.id,
          name: person.label,
          // The card falls back role → title → email, so an empty role would
          // resurrect the stale imported title (or worse, the email). Name the
          // state instead, without claiming Unassigned from incomplete data.
          role: position?.label
            ?? (seatedChart
              ? seatlessRole
              : stringValue(person.props.title) ?? stringValue(person.props.role) ?? ''),
          ...(seatedChart && !position ? { title: seatlessRole } : {}),
          department: department?.label
            ?? stringValue(person.props.department)
            ?? stringValue(person.props.departmentName)
            ?? undefined,
          departmentKey: department?.key,
          directReports,
          successorCount: positionState?.successorCount ?? 0,
          unstaffed: positionState?.vacant ?? false,
          strokeColor: cardAccent,
          borderColor: cardAccent,
          accentColor: cardAccent,
          projectionContext: reducedMembership.reportingChainContextPersonIds.has(person.id)
            ? 'reporting_chain'
            : 'member',
        },
      } as Node;
    });
  // A handover or departure leaves a real position with nobody in it. Person
  // cards alone cannot say that: every reporting line through the vacant seat
  // drops and the subtree beneath it floats off the tree — the chart reads as
  // broken precisely when the org changed on purpose. Render the vacant seat
  // as an open-seat card so the hierarchy stays connected and the backfill is
  // visible where the work is.
  const openSeats = buildOpenSeatProjection(model, includedIds, { personNodeType, viewMode });
  const nodes = [...peopleNodes, ...openSeats.nodes];
  const edges = [
    ...hierarchy.edges.filter(
      (edge) => includedIds.has(edge.from) && includedIds.has(edge.to),
    ),
    ...hierarchy.matrixEdges.filter(
      (edge) => includedIds.has(edge.from) && includedIds.has(edge.to),
    ),
    ...openSeats.edges,
  ];

  if (
    previous
    && topologyMatches(previous, nodes, edges)
    && presentationSizeProfileMatches(previous, nodes)
    && presentationSpacingMatches(previous, layoutOpts)
  ) {
    const priorById = new Map(previous.nodes.map((node) => [node.id, node]));
    for (const node of nodes) {
      const prior = priorById.get(node.id);
      if (prior) Object.assign(node, { x: prior.x, y: prior.y });
    }
  } else {
    const positions = computeInitialPeopleChartLayout(nodes, edges, layoutOpts);
    for (const node of nodes) {
      const point = positions.get(node.id);
      if (point) Object.assign(node, point);
    }
  }

  const now = new Date().toISOString();
  const scene: Scene = {
    version: 1,
    nodes,
    edges,
    meta: { createdAt: previous?.meta?.createdAt ?? now, updatedAt: now },
  };
  peopleChartLayoutSpacing.set(scene, layoutOpts);
  return scene;
}

export function resolvePeopleChartPresentation(
  presentation?: PeopleChartPresentation | null,
): {
  personNodeType: string;
  viewMode: OrgChartViewMode;
  cardAccent: string;
  nodeSpacing: number | undefined;
  verticalSpacing: number | undefined;
} {
  const personNodeType = isOrgPersonNodeType(presentation?.personNodeType)
    ? presentation!.personNodeType!
    : DEFAULT_ORG_PERSON_NODE_TYPE;
  const accentRaw = typeof presentation?.cardAccent === 'string'
    ? presentation.cardAccent.trim()
    : '';
  const cardAccent = /^#[0-9a-f]{6}$/i.test(accentRaw) ? accentRaw : '#10B981';
  return {
    personNodeType,
    viewMode: resolveOrgChartViewMode(presentation?.viewMode),
    cardAccent,
    nodeSpacing: resolveFiniteSpacing(presentation?.nodeSpacing),
    verticalSpacing: resolveFiniteSpacing(presentation?.verticalSpacing),
  };
}

function resolveFiniteSpacing(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

const openSeatNodeId = (positionId: string): string => `open-seat:${positionId}`;

/**
 * Vacant positions that hold the reporting fabric together, as locked
 * open-seat cards plus the reporting edges through them.
 *
 * A seat renders when something on the other side of one of its reporting
 * lines renders — a seated person, or another rendered open seat (a chain of
 * vacancies still connects). A vacant position with no rendered neighbour at
 * all stays hidden: it would float as pure clutter. Redacted or partial
 * occupancy for that seat renders nothing: "vacant" cannot be distinguished
 * from "not shown to you" without the server's per-position completeness.
 */
function buildOpenSeatProjection(
  model: OrgModelProjection,
  includedPersonIds: ReadonlySet<string>,
  presentation: { personNodeType: string; viewMode: OrgChartViewMode },
): { nodes: Node[]; edges: Edge[] } {
  const stateById = new Map(model.positionState.map((state) => [state.positionId, state]));
  const positionsById = new Map(model.positions.map((position) => [position.id, position]));
  const peopleIds = new Set(model.people.map((person) => person.id));

  const relations = model.relationships.filter((relation) => (relation.predicate === 'reports_to'
    || relation.predicate === 'matrix_reports_to')
    && (stateById.has(relation.subjectRef) || stateById.has(relation.objectRef)));

  const occupantOf = (ref: string): string | null => {
    const occupant = seatOccupant(stateById.get(ref));
    return occupant && includedPersonIds.has(occupant) ? occupant : null;
  };
  const isOpenSeat = (ref: string): boolean => {
    const state = stateById.get(ref);
    return Boolean(state && state.occupancyVisibility === 'visible'
      && state.vacant === true && positionsById.has(ref));
  };

  const rendered = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const relation of relations) {
      const ends: Array<[string, string]> = [
        [relation.subjectRef, relation.objectRef],
        [relation.objectRef, relation.subjectRef],
      ];
      for (const [self, other] of ends) {
        if (!isOpenSeat(self) || rendered.has(self)) continue;
        const otherRenders = peopleIds.has(other)
          ? includedPersonIds.has(other)
          : Boolean(occupantOf(other)) || rendered.has(other);
        if (otherRenders) {
          rendered.add(self);
          changed = true;
        }
      }
    }
  }
  if (rendered.size === 0) return { nodes: [], edges: [] };

  const resolveEnd = (ref: string): string | null => {
    if (peopleIds.has(ref)) return includedPersonIds.has(ref) ? ref : null;
    return occupantOf(ref) ?? (rendered.has(ref) ? openSeatNodeId(ref) : null);
  };
  const seen = new Set<string>();
  const edges: Edge[] = [];
  for (const relation of relations) {
    const from = resolveEnd(relation.subjectRef);
    const to = resolveEnd(relation.objectRef);
    if (!from || !to || from === to) continue;
    // Person-to-person lines are the hierarchy's job; only lines that touch an
    // open seat are added here. This applies to matrix reporting too: the
    // relationship belongs to the Position, so it must remain visible when a
    // succession leaves either endpoint vacant.
    if (!from.startsWith('open-seat:') && !to.startsWith('open-seat:')) continue;
    const key = `${relation.predicate}\u0000${from}\u0000${to}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const isMatrix = relation.predicate === 'matrix_reports_to';
    const matrixDefaults = isMatrix
      ? getRelationshipMapping('org', 'matrix_reports')?.defaultEdgeProps ?? {}
      : {};
    edges.push({
      id: `open-seat-${relation.predicate}:${from}:${to}`,
      type: isMatrix ? 'matrix' : 'reporting',
      from,
      to,
      attrs: {
        ...matrixDefaults,
        relation: isMatrix ? 'matrix_reports' : 'reporting',
        predicate: relation.predicate,
        locked: true,
      },
    } as Edge);
  }

  // Seats with reports get the taller card variant, same as managers do, so
  // rows of siblings stay aligned in the tree layout.
  const seatsWithReports = new Set(
    edges.flatMap((edge) => (edge.type === 'reporting' && edge.to.startsWith('open-seat:') ? [edge.to] : [])),
  );

  const nodes: Node[] = Array.from(rendered).map((positionId) => {
    const position = positionsById.get(positionId)!;
    const state = stateById.get(positionId)!;
    const nodeId = openSeatNodeId(positionId);
    // Person-card footprint, open-seat drawing: the full staffing card sits
    // oversized between compact person cards, so the seat takes the same
    // dimensions as its neighbours while `org_open_seat` draws it unmistakably
    // not-a-person (dashed amber, OPEN SEAT eyebrow). The type is grouped with
    // org_position via POSITION_NODE_TYPES, so the property panel and
    // succession gating treat it as the position record it is.
    const dimensions = resolveOrgPersonCardDimensions(
      presentation.personNodeType,
      presentation.viewMode,
      seatsWithReports.has(nodeId),
    );
    return {
      id: nodeId,
      type: 'org_open_seat',
      version: 1,
      category: 'orgchart',
      x: 0,
      y: 0,
      width: dimensions.width,
      height: dimensions.height,
      w: dimensions.width,
      h: dimensions.height,
      name: position.label,
      text: position.label,
      locked: true,
      lockOwner: 'projection',
      attrs: {
        __shapeVersion: 1,
        positionId: position.externalId ?? position.id,
        folderRecordId: position.id,
        reportingPositionId: position.id,
        title: position.label,
        openSeat: true,
        occupancyVisibility: 'visible',
        staffingStatus: 'vacant',
        occupants: [],
        staffedCount: 0,
        vacantCount: 1,
        successorCount: state.successorCount ?? 0,
        accentColor: positionStaffingAccent('vacant'),
      },
    } as Node;
  });

  return { nodes, edges };
}

/**
 * A Position added in a draft on a projected people chart has no occupant, so
 * it draws as the same open-seat card an enacted vacancy leaves behind, sized
 * like its person-card neighbours. Unlike projected seats it stays unlocked
 * and keeps its draft id: the property panel and position commands edit the
 * scenario addition, and the record (with its own projected seat) exists only
 * once the draft applies. Position charts and manual charts carry no
 * projected person cards, so their draft positions keep the staffing card.
 */
export function presentDraftPositionAsOpenSeat(node: Node, sceneNodes: readonly Node[]): Node {
  if (node.type !== 'org_position') return node;
  const personCard = sceneNodes.find(isProjectedOrgPersonCard);
  if (!personCard) return node;
  const personWidth = personCard.width ?? personCard.w ?? 0;
  const viewMode: OrgChartViewMode = personWidth > 0 && personWidth < ORG_PERSON_CARD_SPECS.full.card.text.width
    ? 'compact'
    : 'full';
  const dimensions = resolveOrgPersonCardDimensions(personCard.type, viewMode);
  return {
    ...node,
    type: 'org_open_seat',
    version: 1,
    width: dimensions.width,
    height: dimensions.height,
    w: dimensions.width,
    h: dimensions.height,
    attrs: {
      ...(node.attrs ?? {}),
      __shapeVersion: 1,
      openSeat: true,
      staffingStatus: 'vacant',
      occupants: [],
      staffedCount: 0,
      vacantCount: 1,
      accentColor: positionStaffingAccent('vacant'),
    },
  } as Node;
}

/** Exported for the source-backed editable starter (orgFromSourceSeed),
 *  which reuses the same permissioned person→manager resolution. */
export function buildPeopleHierarchy(model: OrgModelProjection): PeopleHierarchy {
  const peopleById = new Map(model.people.map((person) => [person.id, person]));
  const positionsById = new Map(model.positions.map((position) => [position.id, position]));
  const positionStateById = new Map(model.positionState.map((state) => [state.positionId, state]));
  const positionByPersonId = new Map<string, OrgModelEntity>();
  const seatsByPersonId = new Map<string, OrgModelEntity[]>();

  // A person with several seats is drawn once, at their primary seat. The
  // first seat seen only stands in when no seat names them as primary.
  for (const state of model.positionState) {
    const position = positionsById.get(state.positionId);
    if (!position) continue;
    for (const personId of state.occupantPersonIds) {
      if (!peopleById.has(personId)) continue;
      seatsByPersonId.set(personId, [...(seatsByPersonId.get(personId) ?? []), position]);
      const isPrimary = state.primaryOccupantPersonId === personId;
      if (isPrimary || !positionByPersonId.has(personId)) {
        positionByPersonId.set(personId, position);
      }
    }
  }

  const pairs = new Map<string, { from: string; to: string }>();
  const matrixPairs = new Map<string, { from: string; to: string }>();
  for (const relation of model.relationships) {
    if (relation.predicate !== 'reports_to' && relation.predicate !== 'matrix_reports_to') continue;
    if (peopleById.has(relation.subjectRef) && peopleById.has(relation.objectRef)) {
      const targetPairs = relation.predicate === 'matrix_reports_to' ? matrixPairs : pairs;
      targetPairs.set(`${relation.subjectRef}\u0000${relation.objectRef}`, {
        from: relation.subjectRef,
        to: relation.objectRef,
      });
      continue;
    }
    const reportState = positionStateById.get(relation.subjectRef);
    const managerState = positionStateById.get(relation.objectRef);
    const report = seatOccupant(reportState);
    const manager = seatOccupant(managerState);
    if (!report || !manager || !peopleById.has(report) || !peopleById.has(manager)) continue;
    if (report === manager) continue;
    // Only the primary seat's reporting line is solid. A secondary seat
    // reporting elsewhere is drawn as a dotted line, the same way a matrix
    // relation is, so the person keeps one place in the tree.
    const fromSecondarySeat = relation.predicate === 'reports_to'
      && positionByPersonId.get(report)?.id !== relation.subjectRef;
    const targetPairs = relation.predicate === 'matrix_reports_to' || fromSecondarySeat
      ? matrixPairs
      : pairs;
    targetPairs.set(`${report}\u0000${manager}`, { from: report, to: manager });
  }

  const managerByPersonId = new Map<string, string>();
  const edges = Array.from(pairs.values()).map(({ from, to }) => {
    if (!managerByPersonId.has(from)) managerByPersonId.set(from, to);
    return {
      id: `people-reports-to:${from}:${to}`,
      type: 'reporting',
      from,
      to,
      attrs: { relation: 'reporting', predicate: 'reports_to', locked: true },
    } as Edge;
  });
  const matrixDefaults = getRelationshipMapping('org', 'matrix_reports')?.defaultEdgeProps ?? {};
  const matrixEdges = Array.from(matrixPairs.values()).map(({ from, to }) => ({
    id: `people-matrix-reports-to:${from}:${to}`,
    type: 'matrix' as Edge['type'],
    from,
    to,
    attrs: {
      ...matrixDefaults,
      relation: 'matrix_reports',
      predicate: 'matrix_reports_to',
      locked: true,
    },
  }));
  return { edges, matrixEdges, managerByPersonId, positionByPersonId, seatsByPersonId };
}

/**
 * The person a seat resolves to for drawing purposes: its primary occupant,
 * or its only occupant when the seat is held as a secondary role. A seat
 * held only as someone's secondary role is still staffed; without this
 * fallback every report into that seat would float off the tree.
 */
function seatOccupant(state: PositionComputedState | undefined): string | null {
  if (!state) return null;
  if (state.primaryOccupantPersonId) return state.primaryOccupantPersonId;
  return state.occupantPersonIds.length === 1 ? state.occupantPersonIds[0]! : null;
}

function talentAttributes(
  model: OrgModelProjection,
): Map<string, Record<string, unknown>> {
  const result = new Map<string, Record<string, unknown>>();
  for (const observation of model.talent.observations) {
    const attrs = result.get(observation.subjectRef) ?? {};
    const key = observation.dimension === 'flight_risk'
      ? 'flightRisk'
      : observation.dimension === 'impact_of_loss'
        ? 'impactOfLoss'
        : observation.dimension;
    attrs[key] = observation.normalizedBand ?? observation.observedValue;
    result.set(observation.subjectRef, attrs);
  }
  return result;
}

function visibleAttributes(
  props: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (key.startsWith('_') || key === 'source') continue;
    result[key] = value;
  }
  return result;
}

function personPositionWorkContext(
  personProps: Readonly<Record<string, unknown>>,
  position: OrgModelEntity | undefined,
): Record<string, unknown> {
  if (!position) return {};
  const result: Record<string, unknown> = {};
  for (const field of ['costCenter', 'location'] as const) {
    if (stringValue(personProps[field])) continue;
    const positionValue = stringValue(position.props[field]);
    if (positionValue) result[field] = positionValue;
  }
  return result;
}

function topologyMatches(
  previous: Scene,
  nodes: readonly Node[],
  edges: readonly Edge[],
): boolean {
  if (previous.nodes.length !== nodes.length || previous.edges.length !== edges.length) return false;
  const nodeIds = new Set(previous.nodes.map((node) => node.id));
  if (nodes.some((node) => !nodeIds.has(node.id))) return false;
  const priorEdges = new Set(previous.edges.map((edge) => `${edge.from}\u0000${edge.to}`));
  return edges.every((edge) => priorEdges.has(`${edge.from}\u0000${edge.to}`));
}

/** True when card type/size profile is unchanged — safe to keep prior x/y. */
function presentationSizeProfileMatches(
  previous: Scene,
  nodes: readonly Node[],
): boolean {
  const priorById = new Map(previous.nodes.map((node) => [node.id, node]));
  for (const node of nodes) {
    const prior = priorById.get(node.id);
    if (!prior) return false;
    if (prior.type !== node.type) return false;
    if (prior.width !== node.width || prior.height !== node.height) return false;
    if (prior.w !== node.w || prior.h !== node.h) return false;
  }
  return true;
}

/** True when tree spacing prefs match the spacing used for the prior scene. */
function presentationSpacingMatches(
  previous: Scene,
  spacing: PeopleChartLayoutSpacing,
): boolean {
  const prior = peopleChartLayoutSpacing.get(previous);
  if (!prior) {
    return spacing.nodeSpacing === undefined && spacing.verticalSpacing === undefined;
  }
  return prior.nodeSpacing === spacing.nodeSpacing
    && prior.verticalSpacing === spacing.verticalSpacing;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Every seat a person holds, primary first, so the card can show the extra
 * roles instead of hiding them behind the one seat the tree places them in.
 */
function personRoleAttributes(
  seats: readonly OrgModelEntity[],
  primary: OrgModelEntity | undefined,
): Record<string, unknown> {
  if (seats.length <= 1) return {};
  const ordered = [
    ...(primary ? [primary] : []),
    ...seats.filter((seat) => seat.id !== primary?.id),
  ];
  return {
    roles: ordered.map((seat) => ({
      positionId: seat.id,
      title: seat.label,
      department: stringValue(seat.props.department) ?? undefined,
      fte: seat.props.fte ?? undefined,
      primary: seat.id === primary?.id,
    })),
    roleCount: ordered.length,
  };
}
